<script setup lang="ts">
import { TaskItemDto } from '@/api/generator';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import FormInputDisplay from '@/components/forms/display/FormInputDisplay.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { info } from '@/components/icons/icons.ts';
import { I18nKey } from '@/utils/typings.ts';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';

const values = useFormValues<TaskItemDto>();
const mode = useFormMode();
const { t } = useTranslation();
const category = t(`RtSystemCategoryEnum.${values.value.category}` as I18nKey);
const subCategory = t(`MedicalSystemSubCategoryEnum.${values.value.subCategory}` as I18nKey);
const taskTypeLabel = t(`TaskTypeDescriptionEnum.${values.value.taskType}` as I18nKey);
const taskCategoryLabel = category + ' | ' + subCategory;
</script>

<template>
  <FormSection class="custom-form-section">
    <v-row>
      <v-col v-if="values.taskType === 'BeamConstancyCheck' && mode !== 'details'" cols="12">
        <v-alert
          class="v-alert-overwrite mb-6"
          type="info"
          :icon="info"
          density="default"
          variant="tonal"
        >
          <template #default>
            {{ $t('DailyCheckWithUnicheckMsg') }}
          </template>
        </v-alert>
      </v-col>
    </v-row>
    <v-row class="justify-sm-start">
      <v-col cols="3">
        <VeeTextField :label="$t('Name')" name="name" :placeholder="$t('Required')" />
        <VeeTextarea :label="$t('Description')" name="description" class="gutterTop" />
        <VeeAttachments
          class="mt-0"
          name="attachments"
          :mode="mode"
          data-testid="Description_Attachments"
        />
      </v-col>

      <v-col cols="7">
        <FormLabel name="taskType" :label="$t('TaskType_one')" mode="details">
          <FormInputDisplay :value="taskTypeLabel" />
        </FormLabel>
        <FormLabel
          name="taskCategory"
          :label="`${$t('Category_one')} | ${$t('Type_one')}`"
          mode="details"
          class="gutterTop"
        >
          <FormInputDisplay :value="taskCategoryLabel" />
        </FormLabel>
      </v-col>

      <v-col cols="2">
        <VeeAvailability
          name="isAvailable"
          :isReferencedBy="values.isReferencedBy ?? undefined"
          :hideReferences="true"
          :impactMessage="
            $t('DisableTaskItemInfoMsg', {
              entityName: $t('Task_one'),
            })
          "
        />
        <EntityChangeBlock v-if="mode === 'details'" :entity="values" />
      </v-col>
    </v-row>
  </FormSection>
</template>

<style scoped>
.custom-form-section {
  padding: 0 !important;
}
</style>
